import './css/App.css';
import Navbar from "./components/Navbar";
import Footer from './components/Footer';
import Newsletter from './components/Newsletter';
import Hero from "./pages/public/Hero";
import About from './pages/public/About';
import Services from './pages/public/Services';
import ServicesTwo from './pages/public/ServicesTwo';
import Team from './pages/public/Team';
import RecentPosts from './pages/public/RecentPosts';
import Quote from './pages/public/quote/Quote';
import Contact from './pages/public/Contact';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsofService from './pages/public/TermsofService';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ForgotPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import NewsletterVerify from './pages/public/NewsletterVerify';
import NewsletterUnsubscribe from './pages/public/NewsletterUnsubscribe';
import AdminDashboard from './pages/core/admin/Dashboard';
import ClientDashboard from './pages/core/clients/Dashboard';
import LoadingWidget from './components/LoadingWidget';
import { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AlertMessage from './components/AlertMessage';

// Protected Route Component
const RoleBasedRoute = ({ children, requireSuperuser = false }) => {
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const isSuperuser = user.is_superuser || user.is_staff;

  if (requireSuperuser && !isSuperuser) {
    return <Navigate to="/client-dashboard" replace />;
  }

  if (!requireSuperuser && isSuperuser) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Guard: redirect already-logged-in users away from /login
const AlreadyLoggedInGuard = ({ children }) => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);

  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperuser = user.is_superuser || user.is_staff;
  const destination = isSuperuser ? '/dashboard' : '/client-dashboard';

  useEffect(() => {
    if (!token) return;

    setAlert({
      type: 'warning',
      title: 'Already Logged In',
      message: 'You are already logged in. Redirecting you to your dashboard...',
      duration: 3000,
    });

    const timer = setTimeout(() => {
      navigate(destination, { replace: true });
    }, 3000);

    return () => clearTimeout(timer);
  }, [token, destination, navigate]);

  if (token) {
    return (
      <>
        {alert && (
          <AlertMessage
            type={alert.type}
            title={alert.title}
            message={alert.message}
            duration={alert.duration}
            onDismiss={() => setAlert(null)}
            showCloseButton={false}
          />
        )}
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50">
          <p className="text-gray-500 text-sm">Redirecting...</p>
        </div>
      </>
    );
  }

  return children;
};

// Home Page Component
const HomePage = () => (
  <>
    <main className="main">
      <Navbar />
      <Hero />
      <Services />
      <About />
      <ServicesTwo />
      <RecentPosts />
      <Team />
      <Contact />
      <Newsletter />
    </main>
    <Footer />
  </>
);

// OAuth Callback Handler Component - Simple loading overlay
const OAuthCallbackHandler = () => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('Processing login...');
  const API_URL = process.env.REACT_APP_API_URL;
  const FRONTEND_URL = process.env.REACT_APP_CALLBACK_URL;

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (error) {
      navigate('/login', { replace: true });
      return;
    }

    if (code && state && !processing) {
      setProcessing(true);
      setMessage('Authenticating...');

      // Determine provider from state
      let provider = null;
      if (state.includes('google')) provider = 'google';
      else if (state.includes('github')) provider = 'github';
      else if (state.includes('facebook')) provider = 'facebook';

      if (provider) {
        // Process the OAuth callback
        const processOAuth = async () => {
          try {
            const csrfToken = getCookie('csrftoken');
            const headers = {
              'Content-Type': 'application/json',
              ...(csrfToken && { 'X-CSRFToken': csrfToken }),
            };
            setMessage(`Completing ${provider} login...`);

            // Different approach for GitHub vs Google/Facebook
            let tokenResponse;

            if (provider === 'github') {
              // GitHub needs a different endpoint
              tokenResponse = await axios.post(`${API_URL}/rest-auth/github/login/`, {
                code,
                redirect_uri: `${API_URL}/`
              },
                { headers, withCredentials: true });
            } else {
              // Google and Facebook use the login endpoint
              tokenResponse = await axios.post(`${API_URL}/rest-auth/${provider}/login/`, {
                code,
                redirect_uri: `${API_URL}/`
              },
                { headers, withCredentials: true });
            }

            const accessToken = tokenResponse.data.access;
            const refreshToken = tokenResponse.data.refresh;

            setMessage('Fetching user details...');

            // Get user details
            const userResponse = await axios.get(`${API_URL}/rest-auth/user-request/`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`
              }
            });

            const userData = userResponse.data[0];

            setMessage('Loading user profile...');

            // Fetch user profile
            const profileResponse = await axios.get(`${API_URL}/rest-auth/user-profile/details/`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`
              }
            }).catch(() => ({
              data: {
                profile: {
                  phone: '',
                  address: '',
                  company_name: '',
                  contact_person: '',
                  created_at: null,
                  updated_at: null
                }
              }
            }));

            const profileData = profileResponse.data;

            // Combine user data with profile
            const userWithProfile = {
              ...userData,
              profile: profileData.profile || {
                phone: '',
                address: '',
                company_name: '',
                contact_person: '',
                created_at: null,
                updated_at: null
              }
            };

            setMessage('Completing setup...');

            // Store in localStorage
            localStorage.setItem('authToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(userWithProfile));
            localStorage.setItem('userId', userData.id);
            localStorage.setItem('username', userData.username);
            if (userData.is_superuser) {
              localStorage.setItem('is_superuser', 'true');
            }

            // Redirect based on user role
            const isSuperuser = userData.is_superuser || userData.is_staff;
            const redirectPath = isSuperuser ? '/dashboard' : '/client-dashboard';

            navigate(redirectPath, { replace: true });

          } catch (err) {
            navigate('/login?error=auth_failed', { replace: true });
          }
        };

        processOAuth();
      } else {
        navigate('/login', { replace: true });
      }
    }
  }, [navigate, API_URL, FRONTEND_URL, processing]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <LoadingWidget />
        <p className="mt-4 text-gray-600 font-medium">{message}</p>
        <p className="mt-2 text-sm text-gray-500">Please wait while we complete your login</p>
      </div>
    </div>
  );
};

function App() {
  const location = useLocation();
  const [isOAuthCallback, setIsOAuthCallback] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
      mirror: true,
    });
  }, []);

  // Check if current URL contains OAuth callback parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasCode = params.has('code');
    const hasState = params.has('state');
    setIsOAuthCallback(hasCode && hasState);
  }, [location]);

  // Scroll to hash section after navigating from another page (e.g. /#services-2)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      let attempts = 0;
      const interval = setInterval(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          clearInterval(interval);
        } else if (++attempts > 20) {
          clearInterval(interval); // Give up after ~2s
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [location]);

  return (
    <>
      <Routes>
        {/* OAuth Callback Route - Only renders when callback parameters are present */}
        <Route path="/" element={
          isOAuthCallback ? <OAuthCallbackHandler /> : <HomePage />
        } />

        <Route path="/login" element={
          <AlreadyLoggedInGuard>
            <Navbar />
            <Login />
          </AlreadyLoggedInGuard>
        } />

        <Route path="/quote" element={
          <>
            <Navbar />
            <Quote />
          </>
        } />

        <Route path="/signup" element={
          <>
            <Navbar />
            <Signup />
          </>
        } />
        <Route path="/privacy-policy" element={
          <>
            <Navbar />
            <PrivacyPolicy />
            <Footer />
          </>
        } />
        <Route path="/terms-of-service" element={
          <>
            <Navbar />
            <TermsofService />
            <Footer />
          </>
        } />
        <Route path="/forgot-password" element={
          <>
            <Navbar />
            <ForgotPassword />
          </>
        } />

        <Route path="/reset-password/:uidb64/:token/" element={
          <>
            <Navbar />
            <ResetPassword />
          </>
        } />

        <Route path="/verify-email/:key/" element={
          <>
            <Navbar />
            <VerifyEmail />
          </>
        } />
        <Route path="/verify-email" element={
          <>
            <Navbar />
            <VerifyEmail />
          </>
        } />
        <Route path="/newsletter/verify/:token/" element={<NewsletterVerify />} />
        <Route path="/newsletter/verified/" element={<NewsletterVerify />} />
        <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />

        {/* Admin Dashboard - for superusers only */}
        <Route path="/dashboard" element={
          <RoleBasedRoute requireSuperuser={true}>
            <AdminDashboard />
          </RoleBasedRoute>
        } />

        {/* Client Dashboard - for regular users */}
        <Route path="/client-dashboard" element={
          <RoleBasedRoute requireSuperuser={false}>
            <ClientDashboard />
          </RoleBasedRoute>
        } />

        <Route path="/dashboard/*" element={
          <RoleBasedRoute requireSuperuser={true}>
            <AdminDashboard />
          </RoleBasedRoute>
        } />

        <Route path="/home" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;